import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Users, Mail, Database, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MandalaCorner } from '../components/luxury/MandalaCorner';
import { api } from '../App';
import { toast } from 'sonner';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

export default function LuxuryPrivacyPolicy() {
  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--lux-royal-soft))] to-[hsl(var(--lux-ivory))] overflow-hidden">
        <MandalaCorner position="top-left" className="opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Shield className="w-16 h-16 text-[hsl(var(--lux-gold))] mx-auto mb-6" />
            <h1 className="text-5xl sm:text-6xl font-bold text-[hsl(var(--lux-royal))] mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>
              Privacy <span className="text-[hsl(var(--lux-gold))]">Policy</span>
            </h1>
            <p className="text-lg text-[hsl(var(--lux-ink)/0.8)] max-w-2xl mx-auto">
              How we protect your personal data under the UK GDPR and the Data Protection Act 2018
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Points */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Lock, title: 'Secure Data', desc: 'Your information is protected with industry-standard encryption' },
              { icon: Eye, title: 'Transparency', desc: 'We clearly explain how we collect and use your data' },
              { icon: Users, title: 'Your Rights', desc: 'You control your personal information and can request deletion' },
            ].map((item, idx) => (
              <motion.div key={idx} {...fadeInUp} transition={{ delay: idx * 0.1 }}>
                <Card className="lux-ornate-card text-center h-full">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center">
                      <item.icon className="w-8 h-8 text-[hsl(var(--lux-royal))]" />
                    </div>
                    <h3 className="text-lg font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
                      {item.title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--lux-ink)/0.7)]">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16 bg-[hsl(var(--lux-parchment))]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {[
            {
              title: '1. Who we are (data controller)',
              icon: Shield,
              content: 'Gujarat Samaj Nottingham ("GSN", "we", "us") is a registered charity (Charity No. 513936) at 28 Handel Street, Nottingham NG3 1JE. We are the data controller for the personal information collected through this website and our membership, events, Saturday Club and communications, and we process it in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. Data protection queries: info@gujaratsamajnottingham.com.'
            },
            {
              title: '2. Information we collect',
              icon: Database,
              content: 'Membership: name, date of birth (optional for existing members claiming their membership), gender, email, phone, postal address and postcode, membership type and dates, GS membership number, family links (parent/guardian for members under 14), and — where you choose to provide them — a photo ID document (passport, driving licence or national ID) and a photograph for your membership card. Events and tickets: the name of each attendee, your email and phone, and ticket QR codes. Payments: amount, date and Stripe payment reference (card details go directly to Stripe and never touch our systems). Saturday Club, volunteering and enquiries: the details you give us on those forms. Communications: newsletter subscription and, for emails we send, delivery information (delivered, bounced, complained) so we stop emailing addresses that fail. Technical: IP address and basic device information in security logs and to limit abusive requests. We do not use facial recognition or any biometric processing of photographs.'
            },
            {
              title: '3. Existing members moving to the digital system',
              icon: Users,
              content: 'Our existing membership records (name, contact details, membership type and expiry) have been transferred into the new digital system. Each existing member receives an individual, personal claim link by email so they can check and update their own details, optionally add a photo ID and photograph, and receive a digital membership card. Where several family members share one email address, each person receives their own separate email and link; one person\u2019s link never gives access to another person\u2019s record. Claim links are personal, time-limited and cannot be used to alter anyone else\u2019s membership. These invitations are service messages about your existing membership, not marketing.'
            },
            {
              title: '4. How we use your information and our lawful bases',
              icon: Eye,
              content: 'To provide your membership, issue your membership number and digital card, and verify membership at events (contract). To process ticket bookings, donations and payments and send confirmations (contract). To verify identity and eligibility — e.g. reviewing a photo ID or age proof for 65+ membership — and to keep accurate records and prevent misuse (legitimate interests; you may choose not to provide documents). To send newsletters and event news you have subscribed to (consent, withdrawable at any time via the unsubscribe link). To send occasional information about our events to current members (legitimate interests, with an opt-out in every email). To keep the charity\u2019s financial and governance records (legal obligation). To keep the site secure, prevent fraud and investigate problems (legitimate interests).'
            },
            {
              title: '5. Children',
              icon: Users,
              content: 'Memberships for children under 14 are held under a parent or guardian, who provides and controls the child\u2019s information. We ask for a parent or guardian to be linked before a child\u2019s membership is verified. We do not knowingly collect information directly from children under 13 without a parent or guardian.'
            },
            {
              title: '6. Automated assistance and decisions',
              icon: Eye,
              content: 'When a photo ID is uploaded, software may read the name and date of birth from the image to pre-fill the check for our team. No decision about your membership is made solely by automated means: every verification is reviewed and approved by an authorised administrator, and you can contact us to query any outcome.'
            },
            {
              title: '7. Who we share information with',
              icon: Users,
              content: 'We do not sell, trade or rent personal information. We use a small number of service providers acting on our instructions under contract: Stripe (payments); Amazon Web Services (sending our emails, hosted in the AWS London region); Ideal Postcodes and Postcodes.io (finding your address from a postcode you enter — only the postcode is sent); and our website hosting and database provider. Authorised committee members and administrators access records only for the purposes above, behind an additional database password, and their actions are recorded in an audit log. We may disclose information where required by law or to protect the charity, our members or the public.'
            },
            {
              title: '8. International transfers',
              icon: Lock,
              content: 'Our email is sent from within the UK (AWS London). Some providers, such as Stripe, may process data outside the UK. Where that happens we rely on the UK Government\u2019s adequacy regulations or the ICO\u2019s International Data Transfer Agreement / Addendum to the EU Standard Contractual Clauses, so that your information receives equivalent protection.'
            },
            {
              title: '9. How long we keep information',
              icon: Database,
              content: 'Membership records: for the life of the membership and up to 6 years after it ends, for charity record-keeping. Photo ID documents: reviewed for verification and then deleted under our retention schedule (currently within 6 months of upload, configurable by the committee), with access restricted to authorised staff; you may ask us to delete an ID document earlier once verification is complete. Membership card photographs: while your membership is active and for up to 12 months after it ends. Financial and donation records: 6 years, as required by UK charity and tax law. Ticket and attendee data: until after the event plus a short reconciliation period. Newsletter details: until you unsubscribe. Claim links expire after 5 days and claim sessions after 7 days. Security and audit logs: up to 12 months unless needed for an investigation.'
            },
            {
              title: '10. Membership cards and QR codes',
              icon: Lock,
              content: 'Your digital membership card shows your name, GS membership number, membership type and expiry, your photograph if you have provided one, and a QR code. When the QR code is scanned at an event it shows the verifier only your name, membership number, membership status and — where provided — your photograph, so that your membership can be confirmed. Ticket QR codes work the same way for the named attendee. Neither code contains your address, date of birth or documents.'
            },
            {
              title: '11. Keeping your information secure',
              icon: Lock,
              content: 'We use appropriate technical and organisational measures: encrypted connections (HTTPS), private storage for documents and photographs, signed and expiring links, an additional password for the member database, role-based staff access, rate-limiting and audit logs. Payment card details are handled only by Stripe. No system is completely secure; if a personal data breach is likely to result in a risk to you, we will inform you and the ICO as the law requires.'
            },
            {
              title: '12. Your rights',
              icon: Shield,
              content: 'Under the UK GDPR you have the right to: be informed; access a copy of your data; have inaccurate data corrected; have data erased ("right to be forgotten") where there is no legal reason to keep it; restrict or object to processing (including direct marketing, which we will always stop on request); data portability; withdraw consent at any time; and not be subject to solely automated decisions. Use the request form below or email info@gujaratsamajnottingham.com. We will respond within one month (extendable by two months for complex requests, and we will tell you if so). We may need to confirm your identity before releasing information. There is no fee unless a request is manifestly unfounded or excessive.'
            },
            {
              title: '13. Complaints — Information Commissioner\u2019s Office (ICO)',
              icon: Mail,
              content: 'If you are unhappy with how we handle your personal data you have the right to complain to the UK supervisory authority: the Information Commissioner\u2019s Office, Wycliffe House, Water Lane, Wilmslow SK9 5AF — ico.org.uk/make-a-complaint — 0303 123 1113. We would welcome the chance to resolve your concern first, so please contact us before approaching the ICO.'
            },
            {
              title: '14. Cookies and similar technologies',
              icon: Eye,
              content: 'We use only essential cookies and browser storage needed to run the website — for example to keep you signed in to the staff portal, to remember a membership claim you have started, and to protect against misuse. We do not use advertising, analytics or cross-site tracking cookies, so no cookie consent banner is required beyond the notice shown. You can clear or block cookies in your browser settings; some features may then not work.'
            },
            {
              title: '15. Changes to this policy and how to contact us',
              icon: Mail,
              content: 'We may update this policy when our services or the law change; the date of the current version is shown below, and significant changes will be highlighted on this page. Questions about this policy or our handling of your data: info@gujaratsamajnottingham.com, or write to Gujarat Samaj Nottingham, 28 Handel Street, Nottingham NG3 1JE.'
            },
          ].map((section, idx) => (
            <motion.div key={idx} {...fadeInUp} transition={{ delay: idx * 0.05 }}>
              <Card className="lux-ornate-card">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center flex-shrink-0">
                      <section.icon className="w-6 h-6 text-[hsl(var(--lux-royal))]" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-3" style={{ fontFamily: 'Cormorant Garamond' }}>
                        {section.title}
                      </h2>
                      <p className="text-[hsl(var(--lux-ink)/0.8)] leading-relaxed">
                        {section.content}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}

          <motion.div {...fadeInUp}>
            <DataRequestForm />
          </motion.div>

          <motion.div {...fadeInUp}>
            <Card className="lux-ornate-card border-2 border-[hsl(var(--lux-gold))]">
              <CardContent className="p-6">
                <p className="text-sm text-[hsl(var(--lux-ink)/0.7)] text-center">
                  <strong>Last updated:</strong> 21 September 2026 (version 2.0)
                  <br />
                  Gujarat Samaj Nottingham - Registered Charity No. 513936
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

// UK GDPR data request form — feeds straight to the staff portal
function DataRequestForm() {
  const [form, setForm] = useState({ name: '', email: '', request_type: 'access', details: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error('Please enter your name and email'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/data-requests', form);
      toast.success(res.message);
      setDone(true);
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const TYPES = [
    { value: 'access', label: 'Send me a copy of my data' },
    { value: 'deletion', label: 'Delete my data' },
    { value: 'correction', label: 'Correct my data' },
    { value: 'other', label: 'Other privacy request' },
  ];

  return (
    <Card className="lux-ornate-card" data-testid="data-request-card">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[hsl(var(--lux-royal-soft))] flex items-center justify-center flex-shrink-0">
            <Send className="w-6 h-6 text-[hsl(var(--lux-royal))]" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-[hsl(var(--lux-royal))] mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>
              Make a Data Request
            </h2>
            <p className="text-sm text-[hsl(var(--lux-ink)/0.7)] mb-4">
              Request a copy of your data, ask us to correct it, or ask for it to be deleted. Your request goes
              straight to our team and we will respond to your email within one month.
            </p>
            {done ? (
              <div className="flex items-center gap-2 text-[hsl(var(--lux-royal))] font-semibold" data-testid="data-request-success">
                <Shield className="w-5 h-5 text-[hsl(var(--lux-gold))]" /> Request received — we'll be in touch within one month.
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Your name</Label>
                    <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" data-testid="data-request-name-input" />
                  </div>
                  <div>
                    <Label>Your email</Label>
                    <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@example.com" data-testid="data-request-email-input" />
                  </div>
                </div>
                <div>
                  <Label>What would you like us to do?</Label>
                  <select
                    value={form.request_type}
                    onChange={e => setForm(f => ({ ...f, request_type: e.target.value }))}
                    className="w-full rounded-lg border border-[hsl(var(--lux-gold)/0.4)] bg-white px-3 py-2 text-sm"
                    data-testid="data-request-type-select"
                  >
                    {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Details (optional)</Label>
                  <Textarea value={form.details} onChange={e => setForm(f => ({ ...f, details: e.target.value }))} rows={3} placeholder="Anything that helps us find your records (e.g. membership number)" data-testid="data-request-details-input" />
                </div>
                <Button type="submit" disabled={loading} className="bg-[hsl(var(--lux-royal))] text-white hover:bg-[hsl(var(--lux-royal-2))]" data-testid="data-request-submit-button">
                  {loading ? 'Sending...' : 'Submit Request'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
